<template>

    <div :class="{ 'pointer-events-none': !$page.props.canManageSettings }">

        <div v-if="plan_api" class="flex items-center font-medium flex-row">

            <div class="flex flex-grow items-center flex-shrink-0">
                <img src="/img/webhook.svg" class="h-6 w-6 mr-2" />
                Receive recordings via a Webhook - <docs-link
                    href="https://docs.birdie.so/birdie-docs/birdie-api/webhook-integration-guide" />

            </div>

            <div class="flex items-center gap-x-2 truncate ml-24">

                <span v-if="auto_disabled_at" class="text-red-500 text-xs">
                    Disabled at {{ new Date(auto_disabled_at).toLocaleString() }} after 10 unsuccessful calls
                </span>

                <div v-else-if="plan_api && enabled"
                    class="flex items-center rounded-md border border-gray-200 flex-shrink truncate text-xs mr-4 flex-shrink">
                    <a href="#"
                        class="bg-gray-100 border-r border-gray-200 p-2 hover:text-birdie-blue flex items-center gap-x-2"
                        @click.prevent.stop="openWebhookModal"
                        v-tippy="{ content: 'Open settings', appendTo: 'parent', zIndex: 99999, placement: 'top' }">
                        <span>Endpoint URL</span>
                        <Cog6ToothIcon class="size-4" />
                    </a>
                    <div class="p-2 flex-grow font-mono truncate">
                        {{ webhook.url }}
                    </div>
                </div>
                <div class="flex-shrink-0">
                    <toggle :checked="enabled" @click.prevent.stop @update="toggleWebhook"></toggle>
                </div>

            </div>

        </div>

        <div v-else class="flex items-center justify-between font-medium">
            <span class="flex items-center"><img src="/img/webhook.svg" class="h-6 w-6 mr-2" />
                Receive recordings via a Webhook - <docs-link
                    href="https://docs.birdie.so/birdie-docs/birdie-api/webhook-integration-guide" /></span>
            <UpgradePlan />
        </div>

        <div v-if="plan_api && enabled" class="flex items-center flex-col justify-start mt-5 text-xs gap-x-8">

            <div v-if="lastCalls.length > 0"
                class="flex items-center flex-col w-full rounded-md border border-gray-200 divide-y divide-gray-200 overflow-hidden">

                <div class="flex items-center w-full transition-colors duration-1000" v-for="call in lastCalls"
                    :key="'call_' + call.id"
                    :class="highlightedCallId === call.id ? 'bg-birdie-blue text-white' : 'text-gray-800'"
                    @click="last_call_id_clicked = call.id">
                    <div :class="[last_call_id_clicked === call.id ? 'bg-gray-50' : 'bg-transparent', 'flex items-center w-full hover:bg-gray-50']">
                        <div class="w-1 h-8 flex-shrink-0"
                            :class="last_call_id_clicked === call.id ? 'bg-birdie-blue' : 'bg-transparent'"></div>
                        <a href="#" @click.prevent.stop="showCall(call)"
                            class="cursor-pointer hover:text-birdie-blue border-r border-gray-200 py-2 px-3 flex items-center gap-x-2 font-mono">
                            <span>Inspect</span>
                            <span>
                                <CodeBracketIcon class="size-4" />
                            </span>
                        </a>
                        <div class="py-2 px-3 font-mono truncate">
                            <a :href="route('library.video', { video_uuid: call.meta.video_uuid})" target="_blank"
                                class="hover:text-birdie-blue truncate">Video #{{ call.meta.video_id }}</a>
                        </div>
                        <div class="py-2 px-3 flex-grow font-mono border-l border-gray-200 truncate">
                            <span :key="'CallDate_' + call.id">
                                Sent at: {{ getReadableDate(call.created_at) }}
                            </span>
                        </div>
                        <div class="flex items-center flex-shink-0 gap-x-2 p-2 mr-2 min-w-32 font-mono truncate transition-colors duration-1000"
                            :key="'lastCallResult_' + call.id">
                            <div
                                :class="['flex items-center gap-x-2 transition-opacity duration-300 truncate',
                            call.retrying ? 'opacity-30' : 'opacity-100',
    highlightedCallId !== call.id && call.response_status >= 200 && call.response_status < 300 ? 'text-green-500' : '', highlightedCallId !== call.id && (call.response_status < 200 || call.response_status >= 300) ? 'text-red-500' : '']">
                                <CheckIcon
                                    v-if="call.response_status >= 200 && call.response_status < 300 && !retry_failed"
                                    class="size-4" />
                                <XMarkIcon v-else class="size-4" />
                                <span v-if="retry_failed">{{ retry_failed }}</span>
                                <span v-else>Response
                                    Status
                                    <span class="font-medium">{{ call.response_status }}</span></span>
                            </div>
                        </div>
                        <div :class="[
    retrying ? 'pointer-events-none' : 'cursor-pointer',
    highlightedCallId == call.id ? 'opacity-0' : 'opacity-100',
                    'h-full w-6 p-1 cursor-pointer flex justify-center items-center group flex-shrink-0 rounded-md  hover:bg-birdie-blue p-1 mr-1 transition-opacity duration-1000']"
                            v-tippy="{ content: 'Resend', appendTo: 'parent', zIndex: 99999, placement: 'top' }"
                            @click.prevent.stop="retryCall(call)">
                            <spinner v-if="call.retrying" class="size-3 text-gray-400" />
                            <ArrowUturnRightIcon v-else class="text-gray-800 group-hover:text-white size-3" />
                        </div>
                    </div>
                </div>


                <div v-if="loading_calls" class="flex items-center w-full transition-colors text-transparent">
                    <div class="flex items-center w-full pointer-events-none select-none">
                        <div class="w-1 h-5 flex-shrink-0"></div>
                        <span class="border-r border-gray-200 py-2 px-3 flex items-center gap-x-2 font-mono">
                            <span class="bg-gray-200 skeleton-box rounded-full">Inspect</span>
                            <span>
                                <CodeBracketIcon class="size-4" />
                            </span>
                        </span>
                        <div class="py-2 px-3 font-mono truncate">
                            <span class="bg-gray-200 skeleton-box rounded-full">Video #123456</span>
                        </div>
                        <div class="py-2 px-3 flex-grow font-mono border-l border-gray-200 truncate">
                            <span class="bg-gray-200 skeleton-box rounded-full">
                                Sent at: {{ getReadableDate(Date.now()) }}
                            </span>
                        </div>
                        <div class="flex items-center flex-shink-0 gap-x-2 p-2 mr-2 min-w-32 font-mono truncate ">
                            <div class="flex items-center gap-x-2 truncate">
                                <span class="bg-gray-200 skeleton-box rounded-full">Response
                                    Status
                                    <span class="font-medium">XXX</span></span>
                            </div>
                        </div>
                        <div
                            class="h-full w-6 p-1 cursor-pointer flex justify-center items-center group flex-shrink-0 rounded-md p-1 mr-1 ">
                        </div>
                    </div>
                </div>


            </div>
            <div v-if="lastCalls.length > 0">
                <span v-if="no_more_calls" class=" flex items-center justify-center w-full text-gray-500 mt-2">No more
                    calls.</span>
                <a v-else href="#" @click.prevent.stop="loadPreviousCall"
                    :class="[loading_calls ? 'opacity-0' : 'opacity-100 cursor-pointer', 'flex items-center justify-center w-full text-birdie-blue hover:underline mt-2 transition-opacity duration-300']">
                    <PlusIcon class="size-3 mr-1" /> Show more
                </a>
            </div>
            <div v-else class="flex items-center justify-center w-full text-gray-500">
                No calls yet…
            </div>


            <div v-if="false" class="flex items-center flex-shrink-0">
                <div class="flex items-center rounded-md border border-gray-200">
                    <a href="#" @click.prevent.stop="showLastCall"
                        :class="[lastCall ? 'cursor-pointer hover:text-birdie-blue' : 'cursor-default', 'bg-gray-100 border-r border-gray-200 p-2 flex items-center gap-x-2']"
                        v-tippy="{ content: lastCall ? 'Inspect' : null, appendTo: 'parent', zIndex: 99999, placement: 'top' }">
                        <span>Last Call</span>
                        <span>
                            <CodeBracketIcon class="size-4" />
                        </span>
                    </a>
                    <div class="p-2 flex-grow font-mono">
                        <span v-if="lastCall" :key="'lastCallDate_' + lastCall.id">
                            {{ new Date(lastCall.created_at).toISOString() }}
                        </span>
                        <span v-else class="pr-5">Waiting for the first call…</span>
                    </div>
                    <div v-if="lastCall"
                        class="flex items-center border-l border-gray-200 flex-shink-0 gap-x-2 px-2 min-w-32"
                        :key="'lastCallResult_' + lastCall.id">
                        <div
                            :class="['flex items-center gap-x-2 transition-opacity duration-300', retrying ? 'opacity-30' : 'opacity-100']">
                            <CheckIcon
                                v-if="lastCall.response_status >= 200 && lastCall.response_status < 300 && !retry_failed"
                                class="text-green-500 size-4" />
                            <XMarkIcon v-else class="text-red-500 size-4" />
                            <span v-if="retry_failed" class="text-red-500 font-mono">{{ retry_failed }}</span>
                            <span v-else
                                :class="[lastCall.response_status >= 200 && lastCall.response_status < 300 ? 'text-green-500' : 'text-red-500', 'font-mono']">Status
                                {{ lastCall.response_status }}</span>
                        </div>
                    </div>
                    <div v-if="lastCall"
                        :class="[
                        retrying ? 'pointer-events-none' : 'cursor-pointer',
                    'h-full w-6 p-1 cursor-pointer flex justify-center items-center group flex-shrink-0 rounded-md bg-gray-200 p-1 mr-1']"
                        v-tippy="{ content: 'Resend', appendTo: 'parent', zIndex: 99999, placement: 'top' }"
                        @click.prevent.stop="retryLastCall">
                        <spinner v-if="retrying" class="size-3 text-gray-400" />
                        <ArrowUturnRightIcon v-else class="text-gray-800 group-hover:text-birdie-blue size-3" />
                    </div>
                </div>
            </div>

            <div v-if="false" class="flex items-center rounded-md border border-gray-200 flex-shrink truncate">
                <a href="#"
                    class="bg-gray-100 border-r border-gray-200 p-2 hover:text-birdie-blue flex items-center gap-x-2"
                    @click.prevent.stop="openWebhookModal"
                    v-tippy="{ content: 'Open settings', appendTo: 'parent', zIndex: 99999, placement: 'top' }">
                    <span>Endpoint URL</span>
                    <Cog6ToothIcon class="size-4" />
                </a>
                <div class="p-2 flex-grow font-mono truncate">
                    {{ webhook.url }}
                </div>
            </div>
            <div v-if="false" class="flex items-center flex-grow flex-shrink-0">
                <div class="flex items-center rounded-md border border-gray-200">
                    <div class="bg-gray-100 border-r border-gray-200 p-2">
                        Signing secret
                    </div>
                    <div class="p-2 flex-grow">
                        <span v-if="!reveal" @click="reveal = true" class="flex items-center justify-betwen">
                            <span class="font-mono">{{ webhook.secret.substring(0, 5) }}*********************</span>
                            <span class="cursor-pointer hover:text-birdie-blue px-2">reveal</span>
                        </span>
                        <span v-else class="pr-2 font-mono">{{ webhook.secret}}</span>
                    </div>
                </div>
            </div>


        </div>

        <webhook-detail-modal ref="webhookDetailModal"
            :key="'webhookDetailModal_' + lastCall.id"></webhook-detail-modal>

        <webhook-modal ref="webhookModal" :the_webhook="webhook" :temp_webhook_secret="temp_webhook_secret"
            @callback="saveWebhookResult"></webhook-modal>

    </div>

</template>

<script>
import Toggle from '@/Components/UI/Toggle'
import WebhookModal from '@/Components/Modals/WebhookModal'
import WebhookDetailModal from '@/Components/Modals/WebhookDetailModal'
import UpgradePlan from '@/Components/UI/UpgradePlan.vue'
import { ArrowUturnRightIcon, CheckIcon, XMarkIcon, CodeBracketIcon, PlusIcon } from "@heroicons/vue/20/solid";
import { Cog6ToothIcon } from "@heroicons/vue/24/outline";

import Spinner from '@/Components/UI/Spinner'
import moment from 'moment-timezone'

export default {

    props: ['the_webhook', 'plan_api', 'temp_webhook_secret', 'last_call'],

    components: {
        Toggle, WebhookModal, UpgradePlan, Spinner,
        Cog6ToothIcon, ArrowUturnRightIcon, XMarkIcon,
        CheckIcon, CodeBracketIcon, WebhookDetailModal,
        PlusIcon
    },

    created: function () {
        if (this.the_webhook) {
            this.webhook = this.the_webhook
            this.valid_url = this.webhook.url
        }
        if (this.last_call) {
            this.lastCalls.push(this.last_call)
            this.lastCall = this.last_call
            this.last_call_id_clicked = this.last_call.id
        } else {
            this.no_more_calls = true
        }
        this.moment = moment
    },

    data() {
        return {
            webhook: null,
            reveal: false,
            lastCall: {
                id: 0
            },
            lastCalls: [],
            retrying: false,
            retry_timeout: null,
            retry_failed: null,
            valid_url: null,
            no_more_calls: false,
            highlightedCallId: null,
            loading_calls: false,
            moment: {},
            timezone: "utc",
            last_call_id_clicked: null
        }
    },

    mounted: function () {
        //console.log(this.engine);
    },

    methods: {
        getReadableDate: function (date) {
            let mom = this.moment(date).tz(this.timezone);
            //return mom.format("ddd MMM Do YYYY, HH:mm:ss");
            return mom.format("YYYY-MM-DD HH:mm:ss");
        },
        toggleWebhook: function (checked) {
            if (!this.webhook) {
                this.openWebhookModal()
            } else {
                this.webhook.enabled = checked ? 1 : 0
                this.updateWebhook({
                    webhook_enabled: this.webhook.enabled
                })
            }
        },
        openWebhookModal: function () {
            this.$refs.webhookModal.open()
        },
        updateWebhook: function (params) {
            if (!this.$page.props.canManageSettings) {
                return
            }
            let url = this.$page.props.api_url + '/v1/webhook/save';
            if (this.webhook) {
                if (this.webhook.id) {
                    url += '/' + this.webhook.id;
                }
            }
            axios.post(url, params)
                .then(response => {
                    let options = {
                        type: "default",
                        timeout: 2500,
                        position: "top-right",
                        hideProgressBar: false,
                    }

                    if (response.data && response.status >= 200 && response.status < 300) {
                        this.webhook = response.data
                        this.valid_url = this.webhook.url
                        this.$moshaToast("Setting updated", options)
                    } else {
                        options = {
                            type: "danger",
                            timeout: -1,
                            position: "top-right",
                        }
                        this.$moshaToast("Error: " + response.data.error, options)
                    }
                })
                .catch(e => {
                    console.error(e);
                    let options = {
                        type: "danger",
                        timeout: -1,
                        position: "top-right",
                    }
                    this.$moshaToast("Error: " + e.response.data.error, options)
                });
        },
        saveWebhookResult(obj) {
            if (!obj.webhook_url || !obj.webhook_secret) {
                this.webhook.url = this.valid_url
                return
            }

            if (obj.webhook_url.startsWith('https://') && obj.webhook_secret.length > 0) {
                const params = {
                    webhook_url: obj.webhook_url,
                    webhook_secret: obj.webhook_secret
                }
                this.updateWebhook(params)
            }
        },
        retryCall(call) {
            if (!call) {
                return
            }
            this.retry_failed = null
            this.retrying = true
            call.retrying = true
            this.retry_timeout = setTimeout(() => {
                this.retrying = false
                this.retry_failed = 'Timeout'
                call.retrying = false
            }, 3000)
            this.last_call_id_clicked = call.id
            const url = this.$page.props.api_url + '/v1/webhook/retry/' + call.id;
            const params = {};
            axios.post(url, params).then(response => {
                if (response.data) {
                    //this.lastCall = response.data
                    // push response.data as first element in lastCalls
                    this.lastCalls.unshift(response.data)
                    this.highlightedCallId = response.data.id
                    setTimeout(() => {
                        this.highlightedCallId = null;
                    }, 1000);
                } else {
                    this.retry_failed = 'Error (' + response.status + ')'
                }
            }).catch(error => {
                this.retry_failed = 'Error (' + error.response.status + ')'
            }).finally(() => {
                this.retrying = false
                call.retrying = false
                clearTimeout(this.retry_timeout)
            })
        },
        showCall(call) {
            if (!call) {
                return
            }
            this.last_call_id_clicked = call.id
            this.$refs.webhookDetailModal.open(call)
        },
        loadPreviousCall() {
            if (this.lastCalls.length == 0 || this.loading_calls) {
                return
            }
            this.loading_calls = true
            const last = this.lastCalls[this.lastCalls.length - 1]
            // console.log("last", last)
            const url = this.$page.props.api_url + '/v1/webhook/calls?first_call_id=' + last.id + '&webhook_id=' + last.webhook_id;
            axios.get(url).then(response => {
                if (response.data) {
                    for (let call of response.data) {
                        this.lastCalls.push(call)
                    }
                    if (response.data.length < 10) {
                        this.no_more_calls = true
                    }
                }
            }).catch(error => {
                this.no_more_calls = true
            }).finally(() => {
                this.loading_calls = false
            })
        }
    },

    computed: {
        enabled: {
            get: function () {
                if (!this.webhook) {
                    return false
                }
                return this.webhook.enabled == 1
            },
            set: function (newValue) {
                // do nothing
            }
        },
        auto_disabled_at() {
            if (this.webhook && this.webhook.auto_disabled_at) {
                return this.webhook.auto_disabled_at
            }
            return null
        }
    }
}

</script>

<style scoped>
.skeleton-box {
    position: relative;
}

.skeleton-box::after {
    display: block;
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-size: cover;
    transform: translateX(-100%);
    background: linear-gradient(to right, transparent 0%, rgba(255, 255, 255, 0.5) 50%, transparent 100%);
    animation: shimmer 2s infinite;
    content: '';
}

@keyframes shimmer {
    100% {
        transform: translateX(100%);
    }
}
</style>
